// TODO these are containers / pages
import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Box, Text, Paper, Tooltip, Transition, CloseButton, Button, Drawer } from '@mantine/core';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_RESULT_MAP } from '@queries/map-result';
import { GET_TRIP_PROFILE } from '@queries/trip-profiles';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';
import Breadcrumb from '@components/breadcrumb';
import TitleBar from '@components/title-bar';
import NextNav from '@components/next-nav';
import Map, {
  AttributionControl,
  Layer,
  MapRef,
  NavigationControl,
  ScaleControl,
  FullscreenControl,
  Source,
  ViewStateChangeEvent,
} from 'react-map-gl/mapbox';
import type { GeoJSONFeature, MapMouseEvent, Map as MapboxMap } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { OPACITY, colorExpression } from '@lib/map-results';
import MapSelect from '@components/map-results-select';
import { Nuts } from '@src/types/maps';
import NutsCompare from '@components/nuts-compare';
import ControlPanelGTI from './map-react-control-panel-gti';
// import ControlPanel from './map-react-control-panel-layers';
import Legend from './map-react-legend-overlay';
import { unescoData } from '../data/unesco-geojson';
import MarkerIconSvg from '../assets/mapbox-marker-icon-green.svg';
import SelectLayersRanks from '@components/select-layers-ranks';
import DebugNutsResult from '@components/debug-nuts-result';
import classes from './map-results.module.css';

type Visibility = 'visible' | 'none';

function MapResultsReact() {
  const { clientId, tripProfileId } = useParams();
  const [searchParams] = useSearchParams();
  // Toggle to show/hide results via URL, e.g. ?mapOnly=1 or ?showResults=false
  const showResults = !(searchParams.get('mapOnly') === '1' || searchParams.get('showResults') === 'false');
  const layerColor = colorExpression(); // TODO: prevent this being called on re-renders
  const [nextEnable, setNextEnable] = useState(false);
  const [compareState, setCompareState] = useState<Nuts[]>([]);
  const [viewState, setViewState] = useState({
    latitude: 49.03,
    longitude: 11.44,
    zoom: 3.5,
  });
  const [popUp, setPopUp] = useState({
    visible: false,
    lat: 0,
    lng: 0,
    feature: {} as GeoJSONFeature,
  });
  const [selected, setSelected] = useState({
    visible: false,
    lat: 0,
    lng: 0,
    feature: {} as GeoJSONFeature,
  });
  // Track selected feature id to visually highlight selected region
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | number | null>(null);
  const unescoState = useState<Visibility>('none'); // visible / none
  const wineRegionState = useState<Visibility>('none');
  const countriesState = useState<Visibility>('visible');
  const citiesState = useState<Visibility>('none');
  const [layersOpen, setLayersOpen] = useState(false);
  const [compareMinimized, setCompareMinimized] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugNutsOpen, setDebugNutsOpen] = useState(false);
  const [debugNutsFeature, setDebugNutsFeature] = useState<GeoJSONFeature | null>(null);
  const [debugAllFeatures, setDebugAllFeatures] = useState<GeoJSONFeature[]>([]);
  const [createResultsMap, { loading, error }] = useMutation(CREATE_RESULT_MAP);

  // TODO: remove v (deprecated), not really we need to know "if" we have a saved  dest to enable Next
  const {
    data: profileData,
    loading: profileLoading,
    error: profileError,
  } = useQuery(GET_TRIP_PROFILE, {
    variables: {
      id: tripProfileId || '',
    },
    skip: !tripProfileId,
  });

  const hasCalledMutationRef = useRef(false);
  const mapRef = useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRefCallback = useCallback((ref: MapRef | null) => {
    if (ref !== null) {
      // Set the actual ref we use elsewhere
      mapRef.current = ref;
      const rawMap: MapboxMap | null = (ref as unknown as { getMap?: () => MapboxMap })?.getMap?.() ?? null;

      const loadImage = () => {
        if (rawMap?.hasImage && !rawMap.hasImage('marker-icon')) {
          const img = new Image(20, 48);
          img.onload = () => {
            if (rawMap?.addImage) {
              rawMap.addImage('marker-icon', img, { sdf: false }); // sdf: true if you want to control the color
            }
          };
          img.src = MarkerIconSvg;
        }
      };

      loadImage();
    }
  }, []);

  // Prevent browser-level zoom (pinch or ctrl/cmd + wheel) inside the map container so the map handles it
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        // Block page zoom, allow Mapbox to consume the event for map zoom
        e.preventDefault();
      }
    };
    const preventGesture = (e: Event) => {
      e.preventDefault();
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    // Safari (macOS) gesture events
    el.addEventListener('gesturestart', preventGesture as EventListener, { passive: false } as AddEventListenerOptions);
    el.addEventListener(
      'gesturechange',
      preventGesture as EventListener,
      { passive: false } as AddEventListenerOptions
    );
    el.addEventListener('gestureend', preventGesture as EventListener, { passive: false } as AddEventListenerOptions);

    return () => {
      el.removeEventListener('wheel', onWheel as EventListener);
      el.removeEventListener('gesturestart', preventGesture as EventListener);
      el.removeEventListener('gesturechange', preventGesture as EventListener);
      el.removeEventListener('gestureend', preventGesture as EventListener);
    };
  }, []);

  // Hide base map city labels when our custom "Major Cities" are enabled to avoid collisions
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const [citiesVisibility] = citiesState;
    const rawMap: MapboxMap | null = (map as unknown as { getMap?: () => MapboxMap })?.getMap?.() ?? null;
    if (!rawMap) return;
    const baseCityLabelLayers = ['settlement-major-label', 'settlement-minor-label', 'settlement-subdivision-label'];
    baseCityLabelLayers.forEach((layerId) => {
      try {
        if (rawMap?.getLayer && rawMap?.setLayoutProperty && rawMap.getLayer(layerId)) {
          rawMap.setLayoutProperty(layerId, 'visibility', citiesVisibility === 'visible' ? 'none' : 'visible');
        }
      } catch {
        // ignore
      }
    });
  }, [citiesState]);

  const moveHandler = (changeEvent: ViewStateChangeEvent) => {
    const currViewState = changeEvent.viewState;
    setViewState({
      latitude: currViewState.latitude,
      longitude: currViewState.longitude,
      zoom: currViewState.zoom,
    });
  };

  const clickHandler = (event: MapMouseEvent) => {
    const features: GeoJSONFeature[] = event.target.queryRenderedFeatures(event.point, {
      layers: ['result-map-layer'],
    });
    const clickedFeature = features?.[0];
    if (!clickedFeature) return;

    const allFeatures: GeoJSONFeature[] = event.target.queryRenderedFeatures(event.point);
    // console.log('features (result-map-layer): ', features);
    // console.log('allFeatures: ', allFeatures);
    // console.log('layers: ', mapRef.current?.getStyle().layers);

    if (event.originalEvent.altKey) {
      setDebugNutsFeature(clickedFeature);
      setDebugAllFeatures(allFeatures);
      setDebugNutsOpen(true);
      return;
    }
    // Clear previous highlight
    if (selectedFeatureId !== null) {
      try {
        event.target.setFeatureState(
          { source: 'result-map', sourceLayer: 'gis_composite_maps_ranked', id: selectedFeatureId },
          { selected: false }
        );
      } catch {
        // ignore
      }
    }

    // Highlight newly selected feature
    const newId = (clickedFeature.id ?? (clickedFeature.properties as { nuts_id?: string })?.nuts_id) as
      | string
      | number;
    try {
      event.target.setFeatureState(
        { source: 'result-map', sourceLayer: 'gis_composite_maps_ranked', id: newId },
        { selected: true }
      );
      setSelectedFeatureId(newId);
    } catch {
      // ignore
    }

    setSelected({
      visible: true,
      lat: event.lngLat.lat,
      lng: event.lngLat.lng,
      feature: clickedFeature,
    });
  };

  const mouseMoveHandler = useCallback((event: MapMouseEvent) => {
    const features: GeoJSONFeature[] = event.target.queryRenderedFeatures(event.point, {
      layers: ['result-map-layer'],
    });
    setPopUp({
      visible: true,
      lat: event.lngLat.lat,
      lng: event.lngLat.lng,
      feature: features[0],
    });
  }, []);

  const handleMapSelectClose = () => {
    // Clear visual highlight when closing selection overlay
    const map = mapRef.current;
    const rawMap: MapboxMap | null = (map as unknown as { getMap?: () => MapboxMap })?.getMap?.() ?? null;
    if (rawMap && selectedFeatureId !== null) {
      try {
        rawMap.setFeatureState(
          { source: 'result-map', sourceLayer: 'gis_composite_maps_ranked', id: selectedFeatureId },
          { selected: false }
        );
      } catch {
        // ignore
      }
    }
    setSelectedFeatureId(null);
    setPopUp({ ...popUp, visible: true });
    setSelected({ ...selected, visible: false });
  };

  useEffect(() => {
    if (
      profileData?.singleTripProfile.destinationId &&
      profileData.singleTripProfile.destinationLat &&
      profileData.singleTripProfile.destinationLong
    ) {
      setNextEnable(true);
    }
  }, [profileData]);

  // If compare selection becomes empty, ensure drawer is not stuck in minimized state
  useEffect(() => {
    if (compareState.length === 0) setCompareMinimized(false);
  }, [compareState.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') setDebugOpen((v) => !v);
      if (e.key === 'Escape') setDebugNutsOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (!tripProfileId || hasCalledMutationRef.current) return;
    hasCalledMutationRef.current = true;
    createResultsMap({ variables: { tripProfileId } }).catch((err) => {
      Sentry.captureException(err);
    });
  }, [tripProfileId, createResultsMap]);

  if (loading || profileLoading) return <LoadingData />;
  if (error || profileError) return <GraphqlError error={error} />;

  // Use a fixed viewport between AppShell header and footer (exposed as CSS vars)
  const activeLayersCount = [unescoState[0], wineRegionState[0], countriesState[0], citiesState[0]].filter(
    (v) => v === 'visible'
  ).length;
  return (
    <>
      <Box className={classes.rootViewport} ref={containerRef}>
        {/* Full-bleed map */}
        <Map
          ref={mapRefCallback}
          mapboxAccessToken={`${import.meta.env.MAPBOX_TOKEN}`}
          initialViewState={viewState}
          scrollZoom={true}
          touchZoomRotate={true}
          keyboard={true}
          doubleClickZoom={true}
          style={{ position: 'absolute', inset: 0 }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          projection={{ name: 'mercator' }}
          styleDiffing
          attributionControl={false}
          interactiveLayerIds={['result-map-layer']}
          onMove={moveHandler}
          onClick={clickHandler}
          onMouseMove={mouseMoveHandler}
        >
          <NavigationControl />
          <ScaleControl />
          <FullscreenControl />
          <AttributionControl customAttribution="© Global Thematic Insights" />

          <Source
            id="result-map"
            type="vector"
            // TODO: mvt data doesn't have an id at the top layer, this promotes the nuts_id to the top level
            promoteId={{ gis_composite_maps_ranked: 'nuts_id' }}
            url={`${import.meta.env.MVT_BASE_URL}/results-map?trip_profile_id=${tripProfileId}`}
          >
            <Layer
              id="result-map-layer"
              type="fill"
              source-layer="gis_composite_maps_ranked"
              paint={{
                'fill-color': layerColor,
                // So country borders drawn above are more legible
                'fill-outline-color': showResults ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0)',
                'fill-opacity': showResults ? OPACITY : 0,
              }}
            />
            <Layer
              id="result-map-boundaries"
              type="line"
              source-layer="gis_composite_maps_ranked"
              minzoom={4}
              paint={{
                // Always-drawn NUTS region borders. The fill layer's fill-outline-color is
                // tied to fill-opacity (0 when results are hidden), so it can't show boundaries
                // in jump-to-map mode. This dedicated line layer is independent of fill-opacity.
                'line-color': 'rgba(0,0,0,0.35)',
                'line-width': showResults ? 0.5 : 1,
                // Fade the NUTS mesh in only once zoomed past the continent view — at country
                // level the full subdivision grid is too busy.
                'line-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0, 6, 1],
              }}
            />
            <Layer
              id="result-map-selected-outline"
              type="line"
              source-layer="gis_composite_maps_ranked"
              paint={{
                'line-color': '#AD0003',
                'line-width': 3,
                // Highlight persists as long as a feature is selected, regardless of showResults —
                // in jump-to-map mode (showResults=false) the user still needs to see what they picked.
                'line-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0],
              }}
            />
          </Source>

          {wineRegionState[0] === 'visible' && (
            <Source id="wine-regions" type="vector" url={`${import.meta.env.MVT_BASE_URL}/layer-wine`}>
              <Layer
                id="wine-regions-layer"
                type="fill"
                source-layer="gis_wine_regions"
                paint={{
                  'fill-color': 'purple',
                  'fill-outline-color': 'black',
                }}
                layout={{ visibility: 'visible' }}
              />
              <Layer
                id="wine-regions-label"
                type="symbol"
                source-layer="gis_wine_regions"
                paint={{
                  'text-color': '#F5F5F5',
                }}
                layout={{
                  'text-field': ['get', 'region'],
                  'text-size': 14,
                  'text-optional': true,
                  visibility: 'visible',
                }}
              />
            </Source>
          )}

          <Source id="countries" type="vector" url="mapbox://mapbox.mapbox-streets-v8">
            <Layer
              id="country-borders"
              type="line"
              source-layer="admin"
              filter={['all', ['==', ['get', 'admin_level'], 0], ['==', ['get', 'maritime'], 'false']]}
              paint={{
                'line-color': '#111827',
                'line-opacity': 0.95,
                'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.8, 3, 1.0, 5, 1.8, 8, 2.6, 10, 3.2, 12, 3.8],
              }}
              layout={{ visibility: `${countriesState[0]}` }}
            />
            <Layer
              id="country-labels"
              type="symbol"
              source-layer="place_label"
              filter={['==', ['get', 'class'], 'country']}
              layout={{
                'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
                'text-size': ['interpolate', ['linear'], ['zoom'], 3, 12, 6, 13, 8, 14, 10, 16, 12, 18],
                'text-optional': true,
                'text-allow-overlap': true,
                'symbol-sort-key': 1000,
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-max-width': 8,
                visibility: `${countriesState[0]}`,
              }}
              paint={{
                'text-color': '#0f172a',
                'text-opacity': 0.98,
                'text-halo-color': '#ffffff',
                'text-halo-width': ['interpolate', ['linear'], ['zoom'], 3, 1.4, 6, 1.6, 8, 1.8, 10, 2.0, 12, 2.2],
                'text-halo-blur': 0.4,
                'text-translate-anchor': 'viewport',
              }}
            />

            {/* Major cities (zoom-dependent density) */}
            <Layer
              id="major-city-labels"
              type="symbol"
              source-layer="place_label"
              filter={[
                'all',
                ['==', ['get', 'type'], 'city'],
                [
                  '<=',
                  ['get', 'filterrank'],
                  // More permissive thresholds to show additional cities as you zoom in
                  ['interpolate', ['linear'], ['zoom'], 3, 3, 5, 4, 7, 6, 9, 8, 11, 12],
                ],
              ]}
              layout={{
                'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 3, 11, 6, 12, 8, 13, 10, 14, 12, 16],
                'text-optional': true,
                'text-allow-overlap': true,
                'text-ignore-placement': true,
                'symbol-sort-key': 900,
                visibility: `${citiesState[0]}`,
              }}
              paint={{
                'text-color': '#111827',
                'text-opacity': 0.98,
                'text-halo-color': '#ffffff',
                'text-halo-width': ['interpolate', ['linear'], ['zoom'], 3, 0.9, 6, 1.0, 8, 1.2, 10, 1.4, 12, 1.6],
                'text-halo-blur': 0.2,
              }}
            />

            {/* Towns (only at higher zooms for additional density) */}
            <Layer
              id="town-labels"
              type="symbol"
              source-layer="place_label"
              minzoom={8}
              filter={[
                'all',
                ['==', ['get', 'type'], 'town'],
                ['<=', ['get', 'filterrank'], ['interpolate', ['linear'], ['zoom'], 8, 3, 10, 6, 12, 10, 14, 12]],
              ]}
              layout={{
                'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 8, 10, 10, 12, 12, 14],
                'text-optional': true,
                'text-allow-overlap': true,
                'text-ignore-placement': true,
                'symbol-sort-key': 800,
                visibility: `${citiesState[0]}`,
              }}
              paint={{
                'text-color': '#111827',
                'text-opacity': 0.98,
                'text-halo-color': '#ffffff',
                'text-halo-width': ['interpolate', ['linear'], ['zoom'], 8, 0.6, 10, 0.9, 12, 1.0],
                'text-halo-blur': 0.2,
              }}
            />
          </Source>

          {/* 'icon-image': 'marker-11' */}
          <Source id="unesco" type="geojson" data={unescoData}>
            <Layer
              id="unesco-layer"
              type="symbol"
              layout={{
                'text-field': ['get', 'title'],
                'text-size': 12,
                'text-offset': [0, 1.5],
                'icon-image': 'marker-icon',
                visibility: `${unescoState[0]}`,
              }}
            />
          </Source>
          {selected.visible && (
            <MapSelect
              lat={selected.lat}
              lng={selected.lng}
              feature={selected.feature}
              onClose={handleMapSelectClose}
              setCompareState={setCompareState}
            />
          )}

          <Legend />
        </Map>

        {/* Overlays: top-left content */}
        <Box className={classes.overlayTop}>
          <Paper shadow="md" radius="md" withBorder className={`${classes.overlayCard} ${classes.overlayCardCompact}`}>
            <Box style={{ display: 'inline-block' }}>
              <Breadcrumb pageStep={2} pb={10} />
              <TitleBar p={2} pl={8} pr={8} order={4} fluid={true} />
            </Box>
          </Paper>
        </Box>

        {/* Directions tip: bottom-center (hidden when comparing) */}
        {compareState.length === 0 && (
          <Box className={classes.overlayTip}>
            <Paper shadow="sm" p="xs" radius="md" withBorder>
              <Text ta="center" fz={16}>
                Select or compare regions by clicking on them
              </Text>
            </Paper>
          </Box>
        )}

        {/* Compare tray: bottom-center, appears when selections exist */}
        {compareState.length > 0 && !compareMinimized && (
          <Box className={classes.overlayCompare}>
            <NutsCompare
              compareState={compareState}
              setCompareState={setCompareState}
              onMinimize={() => setCompareMinimized(true)}
            />
          </Box>
        )}

        {/* Minimized compare pill */}
        {compareState.length > 0 && compareMinimized && (
          <Box className={classes.overlayCompareToggle}>
            <Button
              variant="white"
              radius="xl"
              onClick={() => setCompareMinimized(false)}
              aria-label="Show compare drawer"
            >
              {`Compare (${compareState.length})`}
            </Button>
          </Box>
        )}

        {/* Next navigation: bottom-right */}
        <Box className={classes.overlayNext}>
          <NextNav
            pt={0}
            next={`/clients/${clientId}/trip-profiles/${tripProfileId}/selected`}
            saveVisible={false}
            nextDisabled={!nextEnable}
          />
        </Box>

        {/* Layers toggle (top-right) and sliding panel (right) */}
        <Box className={classes.overlayLayers}>
          <Tooltip label={layersOpen ? 'Hide layers' : 'Map layers'} withArrow>
            <Button
              variant="white"
              color="dark"
              radius="xl"
              size="md"
              onClick={() => setLayersOpen((v) => !v)}
              aria-label="Toggle layers panel"
              className={classes.layersPill}
            >
              {activeLayersCount > 0 ? `Layers (${activeLayersCount})` : 'Layers'}
            </Button>
          </Tooltip>
        </Box>
        <Transition mounted={layersOpen} transition="slide-left" duration={180} timingFunction="ease-out">
          {(styles) => (
            <Paper shadow="md" p="sm" radius="md" withBorder className={classes.overlayLayersPanel} style={styles}>
              <Box style={{ position: 'relative' }}>
                <CloseButton
                  aria-label="Close layers"
                  onClick={() => setLayersOpen(false)}
                  style={{ position: 'absolute', top: 6, right: 6, zIndex: 1 }}
                />
                <ControlPanelGTI
                  unescoState={unescoState}
                  wineRegionState={wineRegionState}
                  countriesState={countriesState}
                  citiesState={citiesState}
                />
              </Box>
            </Paper>
          )}
        </Transition>
      </Box>
      {/* Ctrl+Shift+D: layer ranks debug */}
      <Drawer
        opened={debugOpen}
        onClose={() => setDebugOpen(false)}
        position="bottom"
        size="160px"
        withCloseButton={false}
        styles={{ body: { padding: '8px', overflowY: 'auto' } }}
      >
        <SelectLayersRanks />
      </Drawer>
      {/* Shift+Click: NUTS region debug */}
      {debugNutsOpen && debugNutsFeature && (
        <Box
          style={{
            position: 'fixed',
            bottom: 'var(--app-footer-height, 30px)',
            left: 0,
            right: 0,
            height: 340,
            background: 'rgba(255,255,255,0.97)',
            borderTop: '1px solid #dee2e6',
            padding: 8,
            overflowY: 'auto',
            zIndex: 20,
            pointerEvents: 'auto',
          }}
        >
          <CloseButton
            size="xs"
            style={{ position: 'absolute', top: 6, right: 6 }}
            onClick={() => setDebugNutsOpen(false)}
          />
          <DebugNutsResult
            feature={debugNutsFeature}
            allFeatures={debugAllFeatures}
            layerStates={{
              unesco: unescoState[0],
              wineRegion: wineRegionState[0],
              countries: countriesState[0],
              cities: citiesState[0],
            }}
          />
        </Box>
      )}
    </>
  );
}

export default MapResultsReact;
